<script lang="ts">
import { browser, building } from '$app/environment'
import { page } from '$app/state'
import StudySlideStatTileCard from '$lib/components/study/cards/StudySlideStatTileCard.svelte'
import BarChart from '$lib/components/study/charts/BarChart.svelte'
import StudyChapterProblemSlide from '$lib/components/study/slide_types/StudyChapterProblemSlide.svelte'
import StudyChoroplethSlide from '$lib/components/study/slide_types/StudyChoroplethSlide.svelte'
import StudyClosingSlide from '$lib/components/study/slide_types/StudyClosingSlide.svelte'
import StudyCoverSlide from '$lib/components/study/slide_types/StudyCoverSlide.svelte'
import StudyEmergingPipelineSlide from '$lib/components/study/slide_types/StudyEmergingPipelineSlide.svelte'
import StudyMarketBattleSlide from '$lib/components/study/slide_types/StudyMarketBattleSlide.svelte'
import StudyMarketOutlookSlide from '$lib/components/study/slide_types/StudyMarketOutlookSlide.svelte'
import StudyMarketStatsSlide from '$lib/components/study/slide_types/StudyMarketStatsSlide.svelte'
import StudyMeetImxDataSlide from '$lib/components/study/slide_types/StudyMeetImxDataSlide.svelte'
import StudyPatientDemographicsSlide from '$lib/components/study/slide_types/StudyPatientDemographicsSlide.svelte'
import StudyPayerMixNarrativeSlide from '$lib/components/study/slide_types/StudyPayerMixNarrativeSlide.svelte'
import StudySpendTrajectorySlide from '$lib/components/study/slide_types/StudySpendTrajectorySlide.svelte'
import StudyPresentation from '$lib/components/study/StudyPresentation.svelte'
import StudySlide from '$lib/components/study/StudySlide.svelte'
import StudySlideContent from '$lib/components/study/StudySlideContent.svelte'
import { colorPalette } from '$lib/utils/report/chart-config'
import type { PageData } from './$types'
import {
  calcInsuredSpendPct,
  calcSharePct,
  ELI_LILLY_DRUGS,
  findFirstQuarterAtLeastTotalPay,
  findTopAgeCohort,
  formatQuarterLabel,
  maxQuarterlyClaimsInYear,
  NOVO_NORDISK_DRUGS,
  sumClaimsByNames,
} from './calculations'

let { data }: { data: PageData } = $props()

/** When true (?disable_links in URL), end-slide links (Explore/Contact) are hidden; Restart Story stays.
 *  `browser` guard is required: this page is prerendered (see src/routes/+layout.js). */
const disableLinks = $derived(browser && !!page.url.searchParams.get('disable_links'))

/**
 * Optional slide tags from `?additional_slide_tags=tag1,tag2` (comma-separated, case-insensitive).
 * Enables gated slides; e.g. `glp-1-china` shows the China slide; `glp-1-compare-leaders` shows s5a (two leaders).
 * Parsed on server and client from the request URL so SSR matches hydration.
 */
const additionalSlideTags = $derived(
  building
    ? []
    : (page.url.searchParams.get('additional_slide_tags') ?? '')
        .split(',')
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean),
)
const showGlp1ChinaSlide = $derived(additionalSlideTags.includes('glp-1-china'))
const showGlp1CompareLeadersSlide = $derived(additionalSlideTags.includes('glp-1-compare-leaders'))

// ── Display stats interface ─────────────────────────────────────────────────
interface DisplayStats {
  // Quarter metadata
  quarterCount: number
  firstQtrLabel: string
  latestQtrLabel: string
  quarterLabels: string[]
  // Hero stat cards
  firstQtrClaimsK: string
  latestQtrClaimsM: string
  totalClaimsM: string
  totalSpendB: string
  growthPct: string
  latestQtrB: string
  // Baseline (first quarter in series) — slide 2
  baselineClaimsK: string
  baselineSpendM: string
  // Calendar span from quarterly keys (e.g. 2015–2025)
  firstYear: string
  lastYear: string
  dataYearRange: string
  /** Max quarterly claims in 2023 (millions), null if no 2023 quarters */
  peak2023QuarterlyClaimsM: string | null
  /** First quarter national `total_pay` ≥ $5.2B (state CSV → quarterly rollups) vs prior quarter. */
  wegovy52bJump: {
    hitQuarterLabel: string
    hitSpendB: string
    priorQuarterLabel: string
    priorSpendB: string
  } | null
  /** Distinct state codes in IMX aggregates */
  stateCount: number
  // Bar chart scaling denominators
  maxDrug: number
  maxPay: number
  // Manufacturer share (slide 5)
  novoClaimsM: string
  novoSharePct: string
  lillyClaimsM: string
  lillySharePct: string
  novoClaimsTotal: number
  lillyClaimsTotal: number
  // Payer mix (slide 6)
  insuredSpendPct: string
  // Gender demographics (slide 7)
  totalGen: number
  femalePct: string
  malePct: string
  unknownPct: string
  // Age demographics (slide 7)
  topAge: [string, number]
  topAgeLabel: string
  topAgeM: string
}

// ── All display calculations in one place ───────────────────────────────────
function calcDisplayStats(data: PageData, sortedQuarterKeys: string[]): DisplayStats {
  const firstQuarter = data.quarterlyData[sortedQuarterKeys[0]]
  const lastQuarter = data.quarterlyData[sortedQuarterKeys[sortedQuarterKeys.length - 1]]
  const quarterLabels = sortedQuarterKeys.map(formatQuarterLabel)

  const totalDrugs = data.topDrugs.reduce((s, d) => s + d[1], 0)
  const totalPayVol = data.payerData.reduce((s, p) => s + p[1], 0)

  const novoClaims = sumClaimsByNames(data.topDrugs, NOVO_NORDISK_DRUGS)
  const lillyClaims = sumClaimsByNames(data.topDrugs, ELI_LILLY_DRUGS)

  const totalGen = data.genderTotals.reduce((a, b) => a + b, 0)
  const topAge = findTopAgeCohort(data.ageCohorts)

  const cashPay = data.payerData.find((p) => p[0] === 'Cash Pay')?.[1] ?? 0

  const firstYear = sortedQuarterKeys[0].slice(0, 4)
  const lastYear = sortedQuarterKeys[sortedQuarterKeys.length - 1].slice(0, 4)

  const peak2023Claims = maxQuarterlyClaimsInYear(data.quarterlyData, sortedQuarterKeys, 2023)

  /** First quarter with national total_pay ≥ $5.2B vs prior quarter; null if never crossed or no prior quarter (optional timeline callout). */
  const SPEND_52B_THRESHOLD = 5.2e9
  const spend52 = findFirstQuarterAtLeastTotalPay(
    data.quarterlyData,
    sortedQuarterKeys,
    SPEND_52B_THRESHOLD,
  )
  const wegovy52bJump =
    spend52 && spend52.prevKey != null && spend52.prevTotalPay != null
      ? {
          hitQuarterLabel: formatQuarterLabel(spend52.key),
          hitSpendB: (spend52.totalPay / 1e9).toFixed(2),
          priorQuarterLabel: formatQuarterLabel(spend52.prevKey),
          priorSpendB: (spend52.prevTotalPay / 1e9).toFixed(2),
        }
      : null

  return {
    quarterCount: sortedQuarterKeys.length,
    firstQtrLabel: formatQuarterLabel(sortedQuarterKeys[0]),
    latestQtrLabel: formatQuarterLabel(sortedQuarterKeys[sortedQuarterKeys.length - 1]),
    quarterLabels,

    firstQtrClaimsK: (firstQuarter.claims / 1e3).toFixed(0),
    latestQtrClaimsM: (lastQuarter.claims / 1e6).toFixed(1),
    totalClaimsM: (data.stats.totalClaims / 1e6).toFixed(1),
    totalSpendB: (data.stats.totalSpend / 1e9).toFixed(0),
    growthPct: Math.round(data.stats.claimsGrowthPct).toLocaleString(),
    latestQtrB: (data.stats.latestQuarterSpend / 1e9).toFixed(1),

    baselineClaimsK: (firstQuarter.claims / 1e3).toFixed(0),
    baselineSpendM: (firstQuarter.total_pay / 1e6).toFixed(0),

    firstYear,
    lastYear,
    dataYearRange: `${firstYear}\u2013${lastYear}`,
    peak2023QuarterlyClaimsM: peak2023Claims > 0 ? (peak2023Claims / 1e6).toFixed(1) : null,
    wegovy52bJump,
    stateCount: Object.keys(data.stateData).length,

    maxDrug: data.topDrugs[0]?.[1] ?? 1,
    maxPay: data.payerData[0]?.[1] ?? 1,

    novoClaimsM: (novoClaims / 1e6).toFixed(1),
    novoSharePct: calcSharePct(novoClaims, totalDrugs).toFixed(1),
    lillyClaimsM: (lillyClaims / 1e6).toFixed(1),
    lillySharePct: calcSharePct(lillyClaims, totalDrugs).toFixed(1),
    novoClaimsTotal: novoClaims,
    lillyClaimsTotal: lillyClaims,

    insuredSpendPct: calcInsuredSpendPct(cashPay, totalPayVol).toFixed(1),

    totalGen,
    femalePct: calcSharePct(data.genderTotals[0], totalGen).toFixed(1),
    malePct: calcSharePct(data.genderTotals[1], totalGen).toFixed(1),
    unknownPct: calcSharePct(data.genderTotals[2], totalGen).toFixed(1),

    topAge,
    topAgeLabel: topAge[0],
    topAgeM: (topAge[1] / 1e6).toFixed(1),
  }
}

/** Slide s5a: Novo vs Lilly metrics sourced only from IMX CSV aggregates. */
interface LeadersSlideMetrics {
  novo2024SpendB: string
  lilly2024SpendB: string
  novo2025SpendB: string
  lilly2025SpendB: string
  novoYoYpct: string | null
  lillyYoYpct: string | null
  yoyCompareLabel: string
  novoLastQM: string
  lillyLastQM: string
  latestQtrLabel: string
}

const MFR_NOVO = 'NOVO NORDISK'
const MFR_LILLY = 'LILLY'

function manufacturerYearSpendBillions(d: PageData, year: string, manufacturer: string): string {
  const v = d.manufacturerYearlyPay?.[year]?.[manufacturer]
  if (v === undefined || Number.isNaN(v)) return '—'
  return (v / 1e9).toFixed(1)
}

function computeLeadersSlideMetrics(d: PageData, sortedQuarterKeys: string[]): LeadersSlideMetrics {
  const years = Object.keys(d.manufacturerYearlyData).sort()
  let novoYoY: string | null = null
  let lillyYoY: string | null = null
  let yoyCompareLabel = ''
  if (years.length >= 2) {
    const y0 = years[years.length - 2]
    const y1 = years[years.length - 1]
    yoyCompareLabel = `${y1} vs ${y0}`
    const np = d.manufacturerYearlyData[y0]?.[MFR_NOVO] ?? 0
    const nl = d.manufacturerYearlyData[y1]?.[MFR_NOVO] ?? 0
    const lp = d.manufacturerYearlyData[y0]?.[MFR_LILLY] ?? 0
    const ll = d.manufacturerYearlyData[y1]?.[MFR_LILLY] ?? 0
    if (np > 0) novoYoY = (((nl - np) / np) * 100).toFixed(1)
    if (lp > 0) lillyYoY = (((ll - lp) / lp) * 100).toFixed(1)
  }

  const lastQ = sortedQuarterKeys[sortedQuarterKeys.length - 1]
  const mc = lastQ ? (d.quarterlyData[lastQ]?.manufacturerClaims ?? {}) : {}
  const novoQ = mc[MFR_NOVO] ?? 0
  const lillyQ = mc[MFR_LILLY] ?? 0

  return {
    novo2024SpendB: manufacturerYearSpendBillions(d, '2024', MFR_NOVO),
    lilly2024SpendB: manufacturerYearSpendBillions(d, '2024', MFR_LILLY),
    novo2025SpendB: manufacturerYearSpendBillions(d, '2025', MFR_NOVO),
    lilly2025SpendB: manufacturerYearSpendBillions(d, '2025', MFR_LILLY),
    novoYoYpct: novoYoY,
    lillyYoYpct: lillyYoY,
    yoyCompareLabel,
    novoLastQM: (novoQ / 1e6).toFixed(1),
    lillyLastQM: (lillyQ / 1e6).toFixed(1),
    latestQtrLabel: lastQ ? formatQuarterLabel(lastQ) : '',
  }
}

// ── Reactive state ──────────────────────────────────────────────────────────
const quarterKeys = $derived(Object.keys(data.quarterlyData).sort())
const stats = $derived(calcDisplayStats(data, quarterKeys))
const leadersSlide = $derived(computeLeadersSlideMetrics(data, quarterKeys))

const glpSpendTrajectoryTimelineItems = $derived.by(() => {
  const m = stats.wegovy52bJump
  const wegovyBlock =
    m != null
      ? `<p class="bd" style="margin:0.5rem 0 0;">IMX national roll-up: quarterly market spend first crosses <strong>$5.2B</strong> in <strong>${m.hitQuarterLabel}</strong> ($${m.hitSpendB}B vs $${m.priorSpendB}B in ${m.priorQuarterLabel}).</p>`
      : ''
  const peak2023 =
    stats.peak2023QuarterlyClaimsM != null
      ? ` Peak quarterly claims in IMX: ${stats.peak2023QuarterlyClaimsM}M.`
      : ''
  return [
    {
      year: stats.firstYear,
      body: `<strong>Baseline.</strong> ~${stats.baselineClaimsK}K quarterly claims. GLP-1s are niche diabetes drugs. $${stats.baselineSpendM}M/quarter.`,
    },
    {
      year: '2021',
      body: `<strong>Wegovy FDA approval</strong> (June 2021). First obesity breakthrough.${wegovyBlock}`,
    },
    {
      year: '2023',
      body: `<strong>Mounjaro &amp; Zepbound era.</strong> Tirzepatide disrupts.${peak2023}`,
    },
    {
      year: '2024',
      body: '<strong>Wegovy cardiovascular indication.</strong> FDA approval to reduce CV risk in adults with obesity or overweight and established CVD.',
    },
    {
      year: '2025',
      body: '<strong>Ozempic kidney claim.</strong> Expanded approval to reduce kidney disease progression in T2D with chronic kidney disease.',
    },
    {
      year: '2025',
      body: '<strong>Wegovy for MASH.</strong> FDA approval for noncirrhotic metabolic dysfunction&ndash;associated steatohepatitis (MASH) with moderate-to-advanced liver fibrosis.',
    },
    {
      year: '2025',
      body: `<strong>$${stats.latestQtrB}B/quarter.</strong> WHO issues its first global GLP-1 obesity guideline (Dec). IMX: ${stats.latestQtrClaimsM}M quarterly claims.`,
    },
  ]
})

/** Slide s3: Meet IMX Data — imx-grid tiles + narrative cards. */
const glpMeetImxGridTiles = $derived([
  { variant: 'imx-grid' as const, value: String(stats.stateCount), label: 'U.S. States' },
  { variant: 'imx-grid' as const, value: String(stats.quarterCount), label: 'Quarters of Data' },
  {
    variant: 'imx-grid' as const,
    value: String(data.topDrugs.length),
    label: 'GLP-1 Drugs Tracked',
  },
  { variant: 'imx-grid' as const, value: String(data.payerData.length), label: 'Payer Segments' },
])

const glpMeetImxTextCards = $derived([
  {
    title: '📍 State-Level Resolution',
    text: `All ${stats.stateCount} U.S. jurisdictions in the extract. Real prescribing behavior, state by state, every quarter &mdash; not national averages.`,
  },
  {
    title: `📅 ${stats.quarterCount} Quarters of History`,
    text: `${stats.dataYearRange}. Every inflection point, every policy shock, every drug launch is visible in the data.`,
  },
  {
    title: '🔬 Drug-Level Competitive Intelligence',
    text: 'Ozempic vs. Wegovy vs. Mounjaro vs. Zepbound &mdash; claims, spend, and share broken out by drug.',
  },
  {
    title: '💵 Payer Mix Breakdown',
    text: 'Medicare, Medicaid, PBM, Commercial, Cash &mdash; each tracked separately, every single quarter.',
  },
  {
    title: '🧬 Full Patient Demographics',
    text: 'Age cohorts, gender &mdash; every dimension needed to model real patient populations with precision.',
  },
])

/** Slide s5: drug mix insight (`StudyBlueCalloutCard`). */
const glpS5DrugMixCalloutHtml =
  '<strong>IMX tracks every drug on a daily basis.</strong> Sponsor trial narratives (previous slide) should reconcile with claim volume and share on this view &mdash; when they diverge, you have an investable dislocation.'

/** Slide s6: payer segment rows (`StudyPayerMixNarrativeSlide`). */
const glpS6PayerSegmentRows = $derived(
  data.payerData.map(([name, count], i) => ({
    name,
    count,
    color: colorPalette[i % colorPalette.length],
  })),
)
const glpS6SegmentListSubtitle = $derived(`Total claims ${stats.dataYearRange} &middot; IMX Data`)
const glpS6CashInsightText = $derived(
  `${stats.insuredSpendPct}% of spend is covered by payers &mdash; but the cash-pay trajectory is the most direct signal for oral GLP-1 pricing. IMX tracks it daily.`,
)

const glpS7NarrativeCards = $derived([
  {
    icon: '👥',
    title: `${stats.topAgeLabel} Cohort Dominates`,
    text: `The ${stats.topAgeLabel} birth decade leads with ${stats.topAgeM}M claims &mdash; the highest-volume segment. Oral GLP-1 penetration into younger cohorts is the #1 thesis to validate in upcoming quarters.`,
    footerValue: `${stats.topAgeM}M`,
    footerCaption: `${stats.topAgeLabel} cohort &middot; highest volume segment`,
  },
  {
    icon: '⚡',
    title: 'Gender as a Leading Indicator',
    text: 'Novo Nordisk expects oral GLP-1 obesity medicines to take a materially larger share by 2030 than older forecasts implied &mdash; including among men and younger patients paying out of pocket. Phase 3 OASIS 4 reported about <strong>16.6%</strong> mean weight loss at 64 weeks in treatment-adherent adults (oral semaglutide 25 mg vs placebo), with an intention-to-treat average near <strong>13.6%</strong>. The FDA approved oral semaglutide 25 mg as Wegovy in <strong>December 2025</strong> (empty stomach, 30 minutes before food or other oral meds); list pricing near <strong>$149/month</strong> in company communications.',
  },
])

const glpS7InsightHtml =
  '<strong>Why it matters:</strong> Oral uptake &mdash; cash-pay, younger, male &mdash; is trackable in IMX demographics and payer mix. Lilly&rsquo;s oral orforglipron (ATTAIN / ACHIEVE) is a separate adherence story: no food/water restriction, with maintenance data after switching from injectables.'

const glpS8SpendBillions = $derived(quarterKeys.map((q) => data.quarterlyData[q].total_pay / 1e9))
const glpS8SignalCalloutHtml = $derived(
  `<strong style="color:var(--g);">IMX Data is the early warning system.</strong> Every policy shift, every approval, every pricing change &mdash; you'll see its footprint in claims data before it becomes consensus. ${stats.quarterCount} quarters of proof.`,
)

/** Slide s8: outlook timeline (`StudyMarketOutlookSlide`). */
const glpS8OutlookTimelineItems = [
  {
    period: '2026',
    bodyHtml:
      '<strong>Oral GLP-1 at scale.</strong> Oral Wegovy (Dec 2025 approval). Industry outlook: oral obesity GLP-1s could exceed <strong>one-third</strong> of category sales by 2030. IMX payer and cohort data validate share-of-voice.',
  },
  {
    period: '2026',
    bodyHtml:
      '<strong>Semaglutide exclusivity.</strong> Initial loss of exclusivity begins in select markets; biosimilar read-through starts with claims mix and cash-pay spreads. IMX flags substitution early.',
  },
  {
    period: '2026',
    bodyHtml:
      '<strong>Retatrutide (triple agonist).</strong> Phase 3 TRIUMPH program &mdash; topline TRIUMPH-4 showed ~<strong>28.7%</strong> mean weight loss at 68 weeks vs placebo; more Phase 3 readouts expected through 2026. Still investigational.',
  },
  {
    period: '2026-27',
    bodyHtml:
      '<strong>Orforglipron (Lilly) regulatory path.</strong> Obesity filing in motion; type 2 diabetes submission targeted for 2026. ATTAIN-MAINTAIN: maintenance after switching from injectables. No meal-timing restriction vs oral Wegovy&rsquo;s fasting protocol.',
  },
  {
    period: '2027',
    bodyHtml:
      '<strong>Medicare obesity coverage.</strong> A yes could add tens of millions of eligible patients overnight. IMX captures the payer shift in real time.',
  },
  {
    period: '2030',
    bodyHtml:
      '<strong>Global burden &amp; access.</strong> Obesity linked to ~3.7M deaths worldwide in 2024; economic impact on healthcare estimated near <strong>$3 trillion/year</strong> by 2030. WHO projects &lt;10% of people who could benefit will access GLP-1s. <strong>190+</strong> obesity assets in development; the access gap remains the frontier.',
  },
] as const

/** Slide s8b: pipeline table rows (`StudyEmergingPipelineSlide`). */
const glpS8bPipelineRows = [
  {
    company: 'Structure Therapeutics',
    program: 'GSBR-1290',
    snapshotHtml:
      'Oral small-molecule; mid-stage data up to ~11% mean weight loss; moving toward late-stage.',
  },
  {
    company: 'Merck / Hansoh',
    program: 'HS-10535',
    snapshotHtml: 'Oral GLP-1 agonist; early development.',
  },
  {
    company: 'AstraZeneca / Eccogene',
    program: 'ECC5004',
    snapshotHtml: 'Early efficacy signals; favorable tolerability in public updates.',
  },
  {
    company: 'Roche (Carmot)',
    program: 'CT-966',
    snapshotHtml:
      'Early-trial ~6% mean weight loss; Roche also carries multiple Phase II obesity assets (possible entry late decade).',
  },
  {
    company: 'Viking Therapeutics',
    program: 'VK2735',
    snapshotHtml: 'Dual GLP-1/GIP; mid-stage up to ~12% mean weight loss in disclosed work.',
  },
  {
    company: 'Pfizer',
    program: '\u2014',
    snapshotHtml:
      'Discontinued danuglipron over tolerability; re-entered via <strong>Metsera</strong> acquisition and a <strong>YaoPharma</strong> (China) licensing path for an early obesity asset.',
  },
] as const

const glpS8bImxLensHtml =
  '<strong>IMX lens:</strong> U.S. claims remain the cleanest read on near-term share for Novo and Lilly. China approvals and oral / multi-agonist pipelines are leading indicators of <em>how concentrated</em> GLP-1 value will stay &mdash; and where substitution risk appears first (cash pay, employer plans, new entrants).'

const glpS8bDisclaimerHtml =
  'Pipeline stages and percentages reflect public trial disclosures and trade reporting; they are not IMX efficacy estimates.'

/** Slide s2: external market context footnote (`StudyMarketStatsSlide` → `StudyBlueCalloutCard`). */
const glpS2MarketFootnoteHtml =
  '<strong>External market context (2024&ndash;2025):</strong> Industry reporting attributes roughly <strong>$31.1B</strong> combined 2024 revenue to Novo Nordisk&rsquo;s semaglutide franchise (Ozempic, Wegovy, Rybelsus) and <strong>$16.5B</strong> combined to Lilly&rsquo;s tirzepatide brands (Mounjaro about $11.5B; Zepbound about $4.9B). By Q3 2025, tirzepatide products had passed <strong>$10B</strong> in combined quarterly revenue in company reports. These figures anchor why IMX manufacturer and drug mix trends matter.'

// ── Precomputed chart data ───────────────────────────────────────────────────
const mfrYears = $derived(Object.keys(data.manufacturerYearlyData).sort())
const mfrPayMajor = $derived(data.manufacturerPay.filter((m) => m[0] !== 'Other'))
const topTenStates = $derived(
  (Object.entries(data.stateData) as [string, number][]).sort((a, b) => b[1] - a[1]).slice(0, 10),
)
</script>

<svelte:head>
  <title>{data.metadata.title} | IMX Data</title>
  <meta name="description" content={data.metadata.description} />
  <meta property="og:title" content="{data.metadata.title} | IMX Data" />
  <meta property="og:description" content={data.metadata.description} />
  <meta property="og:type" content="article" />
  <meta property="article:published_time" content={data.metadata.publishedDate} />
  <meta property="article:modified_time" content={data.metadata.updatedDate} />
  <link rel="canonical" href={data.metadata.canonicalUrl} />
  {@html `<script type="application/ld+json">${JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'Dataset',
    name: data.metadata.title,
    description: data.metadata.description,
    url: data.metadata.canonicalUrl,
    datePublished: data.metadata.publishedDate,
    dateModified: data.metadata.updatedDate,
    temporalCoverage: data.metadata.dataThrough,
    inLanguage: 'en',
    creator: data.metadata.authors.map((a) => ({ '@type': 'Person', name: a.name, roleName: a.role })),
    publisher: {
      '@type': 'Organization',
      name: 'IMX Data, LLC',
      url: 'https://www.imxresearch.com',
      logo: { '@type': 'ImageObject', url: 'https://www.imxresearch.com/webflow/images/logo-v2-wht.svg' },
    },
    isAccessibleForFree: false,
    license: 'https://www.imxresearch.com/research',
  })}<\/script>`}
</svelte:head>

<StudyPresentation title="{data.metadata.title} | IMX Data" meta={data.metadata}>
  {#snippet children({ goTo, activeSlideId, slideActive })}
    <!-- SLIDE 0: COVER -->
    <StudyCoverSlide
      id="s0"
      tagline={{
        line1: 'The IMX Data Behind',
        line2Emphasis: 'Every Decision',
        line3: 'That Matters',
      }}
      title="GLP-1"
      subtitle={`${stats.quarterCount} quarters of ground truth.`}
      body="This is what IMX Data sees — and what it means for you."
    />

    <!-- SLIDE 1: THE PROBLEM -->
    <StudyChapterProblemSlide
      id="s1"
      active={slideActive('s1')}
      eyebrow="Chapter 01 &middot; The Challenge"
      headline="What if you could see the market<br />more clearly than <em>anyone else?</em>"
      textCards={[
        {
          icon: '🌧️',
          title: 'National Data Masks Truth',
          text: 'Aggregated averages hide critical variation in state-by-state adoption, spend, and payer mix.',
        },
        {
          icon: '⏱️',
          title: 'Lagging Signals',
          text: 'By the time public data surfaces, the market has moved. Competitors have already repositioned.',
        },
        {
          icon: '🧩',
          title: 'Fragmented Sources',
          text: 'Piecing together demographics, drug mix, and geography means months of reconciliation with no single truth.',
        },
      ]}
      highlight={`<p>The global obesity GLP-1 market is projected to grow at roughly <strong style="color:var(--g);">23% CAGR</strong>, with shortages driving multi-billion-dollar manufacturing build-outs. The IMX claims layer is where those macro forces show up as <em>state, payer, and drug-level</em> reality.</p><p style="margin-top:.65rem;">U.S. GLP-1 claim volume grew <strong style="color:var(--g);">${stats.growthPct}%</strong> from our 2015 baseline to today. Every quarter without granular intelligence, you are navigating the fastest-moving drug class in history &mdash; blindfolded.</p>`}
      growthChart={{
        labels: stats.quarterLabels,
        datasets: [
          {
            data: quarterKeys.map((q) => data.quarterlyData[q].claims / 1e6),
            borderColor: '#11a0a5',
            backgroundColor: 'rgba(17,160,165,.13)',
            pointHoverRadius: 3,
          },
        ],
        chartTitle: 'GLP-1 Market Growth',
        chartSubtitle: `Quarterly claims volume &middot; ${stats.dataYearRange} &middot; IMX Data`,
        firstValue: `${stats.firstQtrClaimsK}K`,
        firstLabel: stats.firstQtrLabel,
        growthPctDisplay: `+${stats.growthPct}%`,
        latestValue: `${stats.latestQtrClaimsM}M`,
        latestLabel: stats.latestQtrLabel,
        yTickFormat: (v) => v + 'M',
        tooltipFormat: (v) => ` ${v.toFixed(1)}M claims`,
        maxTicksLimit: 6,
      }}
    />

    <!-- SLIDE 2: THE SCALE -->
    <StudyMarketStatsSlide
      id="s2"
      active={slideActive('s2')}
      eyebrow="Chapter 02 &middot; The Market"
      headline="The GLP-1 market is unlike anything<br />we've seen before."
      statTiles={[
        {
          value: `${stats.totalClaimsM}M`,
          label: `Total U.S. Claims\n${stats.dataYearRange}`,
        },
        {
          value: `$${stats.totalSpendB}B+`,
          label: 'Total Market Spend\nAll-Time',
          valueColor: 'var(--pur)',
        },
        {
          value: `${stats.growthPct}%`,
          label: `Claim Volume Growth\nSince ${stats.firstYear}`,
          valueColor: 'var(--amb)',
        },
        { value: `$${stats.latestQtrB}B`, label: 'Market Spend\nLatest Quarter' },
      ]}
      doughnut={{
        type: 'pie',
        height: '155px',
        labels: mfrPayMajor.map((m) => m[0]),
        data: mfrPayMajor.map((m) => m[1]),
        colors: ['#11a0a5', '#a78bfa'],
        tooltipFormat: (v, label, total) =>
          ` ${label}: $${(v / 1e9).toFixed(1)}B (${total > 0 ? ((v / total) * 100).toFixed(1) : '0.0'}%)`,
        title: 'Manufacturer Share &mdash; Total Payments',
        subtitle: 'Total GLP-1 pay volume by manufacturer &middot; IMX Data',
      }}
      bar={{
        title: 'Claims per Year &mdash; by Manufacturer',
        subtitle: 'Novo Nordisk vs. Eli Lilly &middot; annual claims &middot; IMX Data',
        labels: mfrYears,
        datasets: [
          {
            label: 'Novo Nordisk',
            data: mfrYears.map(
              (y) => (data.manufacturerYearlyData[y]?.['NOVO NORDISK'] ?? 0) / 1e6,
            ),
            backgroundColor: '#11a0a5',
            borderRadius: 2,
          },
          {
            label: 'Eli Lilly',
            data: mfrYears.map((y) => (data.manufacturerYearlyData[y]?.['LILLY'] ?? 0) / 1e6),
            backgroundColor: '#a78bfa',
            borderRadius: 2,
          },
        ],
        showLegend: true,
        yTickFormat: (v) => v + 'M',
        tooltipFormat: (v, label) => ` ${label}: ${v.toFixed(1)}M`,
        height: '160px',
      }}
      footnote={glpS2MarketFootnoteHtml}
    />

    <!-- SLIDE 2b: SPEND TRAJECTORY -->
    <StudySpendTrajectorySlide
      id="s2b"
      active={slideActive('s2b')}
      eyebrow="Chapter 02 &middot; The Market"
      eyebrowStyle="margin-bottom:0.35rem;"
      headline={`$<em>${stats.totalSpendB}B</em> spent.<br />Here's how it happened.`}
      headlineStyle="margin-bottom:0.75rem;"
      chartTitle="Quarterly Spend &mdash; Plan Pay vs. Patient Pay"
      chartSubtitle={`$USD billions &middot; ${stats.dataYearRange} &middot; IMX Data`}
      lineChart={{
        labels: stats.quarterLabels,
        datasets: [
          {
            label: 'Plan Pay',
            data: quarterKeys.map((q) => data.quarterlyData[q].plan_pay / 1e9),
            borderColor: '#11a0a5',
            backgroundColor: 'rgba(17,160,165,.13)',
            borderWidth: 2,
          },
          {
            label: 'Patient Pay',
            data: quarterKeys.map((q) => data.quarterlyData[q].patient_pay / 1e9),
            borderColor: '#ffd166',
            backgroundColor: 'rgba(255,209,102,.08)',
            borderWidth: 1.5,
          },
        ],
        height: '210px',
        yTickFormat: (v) => '$' + v + 'B',
        tooltipFormat: (v, label) => ` ${label}: $${v.toFixed(1)}B`,
        maxTicksLimit: 7,
      }}
      legendRows={[
        { swatchStyle: 'background:var(--g);', label: 'Plan / Insurer Pay' },
        { swatchStyle: 'background:var(--amb);', label: 'Patient Out-of-Pocket' },
      ]}
      chartAfterHtml="The spend story is as dramatic as the claims story &mdash; but the <em>composition</em> matters. Plan pay has surged while patient out-of-pocket compresses, reflecting insurer adoption waves and manufacturer coupon programs."
      statTiles={[
        {
          variant: 'stat-inline',
          value: `$${stats.totalSpendB}B+`,
          label: 'Total Market Spend\nAll-Time',
        },
        {
          variant: 'stat-inline',
          value: `$${stats.latestQtrB}B`,
          label: 'Latest Quarter\nSpend',
          valueColor: 'var(--amb)',
        },
        {
          variant: 'stat-inline',
          value: `$${stats.baselineSpendM}M`,
          label: `${stats.firstYear} Baseline\nQuarterly Spend`,
          valueColor: 'var(--pur)',
        },
      ]}
      statGridColumns={3}
      signal="When patient OOP flattens while plan pay accelerates, it marks an insurer coverage wave — visible in IMX data quarters before analyst reports catch up."
      timelineItems={glpSpendTrajectoryTimelineItems}
    />

    <!-- SLIDE 3: MEET IMX DATA -->
    <StudyMeetImxDataSlide
      id="s3"
      eyebrow="Chapter 03 &middot; The Solution"
      headline="Meet <em>IMX Data.</em><br />The granular claims layer<br />you've been looking for."
      intro="IMX aggregates, normalizes, and delivers U.S. pharmaceutical claims at a depth no other provider matches &mdash; state-level, quarter-by-quarter, segmented by drug, payer, demographics, and geography."
      gridTiles={glpMeetImxGridTiles}
      textCards={glpMeetImxTextCards}
    />

    <!-- SLIDE 4: GEOGRAPHIC -->
    <StudySlide id="s4" extraBottom>
      <StudySlideContent maxWidth="980px">
        <div class="ey">Capability 01 &middot; Geographic Intelligence</div>
        <div class="tc" style="gap:1.9rem;align-items:start;">
          <div>
            <div class="hl" style="margin-bottom:.82rem;">
              <em>The direction</em> of the market matters just as much as the pace of its movement.
            </div>
            <p class="bd" style="margin-bottom:.95rem;">
              National averages mask enormous state-to-state variation. IMX Data delivers
              prescribing intelligence at the state level (even as granular as Zip level) &mdash;
              identify where adoption is accelerating, where payers are resisting, where the next
              opportunity is forming.
            </p>
            <div class="hb" style="margin-bottom:.9rem;">
              <p>
                <strong>Real insight:</strong> When Zepbound received FDA approval, IMX Data revealed
                which states adopted within the first quarter vs. those lagging 6+ months &mdash; exposing
                physician network dynamics invisible nationally.
              </p>
            </div>
            <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:.58rem;">
              {#each data.stats.topStates as [state, claims]}
                <StudySlideStatTileCard
                  variant="geo"
                  value={state}
                  label={`${(claims / 1e6).toFixed(1)}M claims`}
                />
              {/each}
            </div>
          </div>
          <div>
            <div class="cc" style="margin-bottom:.72rem;">
              <div class="ct">Top 10 States by GLP-1 Claims</div>
              <div class="cs">Total claims {stats.dataYearRange} &middot; IMX Data</div>
              <BarChart
                active={slideActive('s4')}
                height="210px"
                labels={topTenStates.map((s) => s[0])}
                datasets={[
                  {
                    data: topTenStates.map((s) => s[1] / 1e6),
                    backgroundColor: topTenStates.map((_, i) => `rgba(17,160,165,${1 - 0.07 * i})`),
                    borderRadius: 4,
                  },
                ]}
                yTickFormat={(v) => v + 'M'}
                tooltipFormat={(v) => ` ${v.toFixed(1)}M claims`}
              />
            </div>
          </div>
        </div>
      </StudySlideContent>
    </StudySlide>

    <!-- SLIDE 4b: CHOROPLETH -->
    <StudyChoroplethSlide
      id="s4b"
      eyebrow="Capability 02 &middot; Geographic Intelligence"
      headlineHtml="GLP-1 claim intensity,<br /><em>state by state.</em>"
      stateData={data.stateData}
      topStates={data.stats.topStates}
      chartTitle={`Total GLP-1 Claims by State · ${stats.dataYearRange}`}
      chartSubtitle="Hover a state to see exact claim volume · IMX Data"
      calloutText={`${stats.stateCount} jurisdictions tracked. Every adoption curve, every payer shift — visible as it happens.`}
    />

    <!-- SLIDE 5a: TWO LEADERS — only when ?additional_slide_tags includes glp-1-compare-leaders -->
    {#if showGlp1CompareLeadersSlide}
      <StudySlide id="s5a">
        <StudySlideContent maxWidth="980px">
          <div class="ey">Capability 03 &middot; Drug Mix &amp; Competitive Intelligence</div>
          <p
            style="margin:-0.15rem 0 .65rem;font-size:.76rem;letter-spacing:.06em;color:var(--mu);text-transform:uppercase;"
          >
            Supporting context &middot; IMX aggregates
          </p>
          <div
            class="hl"
            style="margin-bottom:.75rem;font-size:clamp(1.32rem, 2.4vw, 1.85rem);line-height:1.18;"
          >
            The two leaders &mdash; <em>same class, different story</em>
          </div>
          <div class="s5a-stack">
            <div class="s5a-box s5a-box--table">
              <div class="ct" style="margin-bottom:.28rem;">Novo vs. Lilly (IMX Data)</div>
              <div class="cs" style="margin-bottom:.5rem;">
                Manufacturer totals &amp; yearly / quarterly CSV extracts
              </div>
              <div
                style="overflow-x:auto;border-radius:10px;border:1px solid var(--gbr);background:var(--table-surface, rgba(0,0,0,.12));"
              >
                <table
                  style="width:100%;border-collapse:collapse;font-size:.74rem;line-height:1.38;"
                >
                  <thead>
                    <tr style="background:rgba(17,160,165,.07);">
                      <th
                        style="text-align:left;padding:.35rem .5rem;border-bottom:1px solid var(--gbr);"
                      ></th>
                      <th
                        style="text-align:left;padding:.35rem .5rem;border-bottom:1px solid var(--gbr);color:var(--g);font-family:'DM Serif Display',serif;font-size:.82rem;font-weight:400;"
                      >
                        Novo Nordisk
                      </th>
                      <th
                        style="text-align:left;padding:.35rem .5rem;border-bottom:1px solid var(--gbr);color:var(--pur);font-family:'DM Serif Display',serif;font-size:.82rem;font-weight:400;"
                      >
                        Eli Lilly
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style="padding:.32rem .5rem;color:var(--mu);">Mechanism</td>
                      <td style="padding:.32rem .5rem;">GLP-1 agonist</td>
                      <td style="padding:.32rem .5rem;">Dual GIP / GLP-1</td>
                    </tr>
                    <tr style="background:var(--row-stripe);">
                      <td style="padding:.32rem .5rem;color:var(--mu);">Brands</td>
                      <td style="padding:.32rem .5rem;">Ozempic, Wegovy, Rybelsus</td>
                      <td style="padding:.32rem .5rem;">Mounjaro, Zepbound</td>
                    </tr>
                    <tr>
                      <td style="padding:.32rem .5rem;color:var(--mu);">2024 market spend</td>
                      <td style="padding:.32rem .5rem;">
                        {#if leadersSlide.novo2024SpendB === '—'}
                          &mdash;
                        {:else}
                          ${leadersSlide.novo2024SpendB}B
                        {/if}
                      </td>
                      <td style="padding:.32rem .5rem;">
                        {#if leadersSlide.lilly2024SpendB === '—'}
                          &mdash;
                        {:else}
                          ${leadersSlide.lilly2024SpendB}B
                        {/if}
                      </td>
                    </tr>
                    <tr style="background:var(--row-stripe);">
                      <td style="padding:.32rem .5rem;color:var(--mu);">2025 market spend</td>
                      <td style="padding:.32rem .5rem;">
                        {#if leadersSlide.novo2025SpendB === '—'}
                          &mdash;
                        {:else}
                          ${leadersSlide.novo2025SpendB}B
                        {/if}
                      </td>
                      <td style="padding:.32rem .5rem;">
                        {#if leadersSlide.lilly2025SpendB === '—'}
                          &mdash;
                        {:else}
                          ${leadersSlide.lilly2025SpendB}B
                        {/if}
                      </td>
                    </tr>
                    <tr>
                      <td style="padding:.32rem .5rem;color:var(--mu);">YoY claims growth</td>
                      <td style="padding:.32rem .5rem;">
                        {#if leadersSlide.novoYoYpct !== null}
                          {Number(leadersSlide.novoYoYpct) > 0 ? '+' : ''}{leadersSlide.novoYoYpct}%
                          <span style="color:var(--mu);font-size:.68rem;">
                            ({leadersSlide.yoyCompareLabel})</span
                          >
                        {:else}
                          &mdash;
                        {/if}
                      </td>
                      <td style="padding:.32rem .5rem;">
                        {#if leadersSlide.lillyYoYpct !== null}
                          {Number(leadersSlide.lillyYoYpct) > 0
                            ? '+'
                            : ''}{leadersSlide.lillyYoYpct}%
                          <span style="color:var(--mu);font-size:.68rem;">
                            ({leadersSlide.yoyCompareLabel})</span
                          >
                        {:else}
                          &mdash;
                        {/if}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div class="s5a-box s5a-box--aside">
              <div
                style="padding:.75rem .85rem;border-radius:10px;border:1px solid rgba(167,139,250,.22);background:rgba(167,139,250,.06);"
              >
                <div
                  style="font-size:.58rem;letter-spacing:.1em;text-transform:uppercase;color:var(--pur);margin-bottom:.35rem;"
                >
                  Latest quarter · claims (IMX)
                </div>
                <p class="bd" style="margin:0;font-size:.86rem;line-height:1.5;">
                  <strong>{leadersSlide.latestQtrLabel}</strong> &mdash; manufacturer claim volume
                  in quarterly CSV: Novo Nordisk <strong>{leadersSlide.novoLastQM}M</strong>; Eli
                  Lilly
                  <strong>{leadersSlide.lillyLastQM}M</strong>.
                </p>
              </div>
              <div class="hb" style="margin:0;">
                <p style="margin:0;font-size:.84rem;line-height:1.48;">
                  <strong>Then IMX:</strong> The next slide is <em>your</em> ground truth &mdash; U.S.
                  claims by drug and manufacturer. When trials and labels move markets, the footprint
                  shows up here first.
                </p>
              </div>
              <p style="margin:0;font-size:.68rem;line-height:1.4;color:var(--mu);opacity:.92;">
                Figures from IMX GLP-1 report CSV extracts (manufacturer totals, yearly, quarterly).
              </p>
            </div>
          </div>
        </StudySlideContent>
      </StudySlide>
    {/if}

    <!-- SLIDE 5: DRUG MIX -->
    <StudyMarketBattleSlide
      id="s5"
      active={slideActive('s5')}
      eyebrow="Capability 03 &middot; Drug Mix &amp; Competitive Intelligence"
      headline="Know which drugs are gaining ground<br /><em>before your competition does.</em>"
      doughnut={{
        title: 'Drug Market Share by Claims Volume',
        subtitle: 'Top drugs &middot; All-time &middot; IMX Data',
        labels: data.topDrugs.map((d) => d[0]),
        data: data.topDrugs.map((d) => d[1]),
        colors: colorPalette,
        cutout: '60%',
        tooltipFormat: (v, _label, total) =>
          ` ${(v / 1e6).toFixed(1)}M claims (${total > 0 ? ((v / total) * 100).toFixed(1) : '0.0'}%)`,
      }}
      battleTitle="Manufacturer Battle"
      battleSubtitle="Novo Nordisk vs. Eli Lilly"
      battleLeft={{
        label: 'Novo Nordisk',
        valueDisplay: `${stats.novoClaimsM}M`,
        sharePct: stats.novoSharePct,
        barValue: stats.novoClaimsTotal,
      }}
      battleRight={{
        label: 'Eli Lilly',
        valueDisplay: `${stats.lillyClaimsM}M`,
        sharePct: stats.lillySharePct,
        barValue: stats.lillyClaimsTotal,
      }}
      volumeTitle="Drug-by-Drug Claim Volume"
      volumeSubtitle="All-time totals &middot; IMX Data"
      volumeRows={data.topDrugs}
      volumeMaxCount={stats.maxDrug}
      volumeBarColors={colorPalette}
      volumeFormatValue={(c) => `${(c / 1e6).toFixed(1)}M`}
      calloutBodyHtml={glpS5DrugMixCalloutHtml}
    />

    <!-- SLIDE 6: PAYER MIX -->
    <StudyPayerMixNarrativeSlide
      id="s6"
      active={slideActive('s6')}
      eyebrow="Capability 04 &middot; Payer Mix Intelligence"
      headline="The payer mix tells a story<br /><em>most investors are missing.</em>"
      introHtml="IMX Data separates Medicare, Medicaid, Commercial, PBM, and Cash-pay volumes. The cash-pay segment alone tells you more about pricing strategy than any analyst report."
      segmentListTitle="Payer Segment Breakdown"
      segmentListSubtitleHtml={glpS6SegmentListSubtitle}
      segmentRows={glpS6PayerSegmentRows}
      maxPayCount={stats.maxPay}
      formatSegmentValue={(c) => `${(c / 1e6).toFixed(1)}M`}
      doughnut={{
        title: 'Payer Mix Share',
        subtitle: '% of total claims &middot; IMX Data',
        labels: data.payerData.map((p) => p[0]),
        data: data.payerData.map((p) => p[1]),
        colors: [...colorPalette],
        cutout: '58%',
        tooltipFormat: (v, _label, total) =>
          ` ${(v / 1e6).toFixed(1)}M (${total > 0 ? ((v / total) * 100).toFixed(1) : '0.0'}%)`,
        height: '190px',
      }}
      cashInsightCard={{
        icon: '💵',
        title: 'Cash Pay: The Signal That Matters',
        titleStyle: 'color:var(--amb);',
        cardStyle: 'border-color:rgba(255,209,102,.3);background:rgba(255,209,102,.05);',
        text: glpS6CashInsightText,
      }}
      footnoteHtml="<strong>Compounded GLP-1s:</strong> Industry analyses tie the majority of compounded GLP-1 volume to weight-management use; utilization via telehealth and direct channels often stayed elevated even after national shortage flags for innovator products eased. IMX separates <em>branded, covered</em> volume from cash and alternative pathways &mdash; critical when assessing true branded demand."
    />

    <!-- SLIDE 7: DEMOGRAPHICS -->
    <StudyPatientDemographicsSlide
      id="s7"
      active={slideActive('s7')}
      eyebrow="Capability 05 &middot; Patient Demographics"
      headline="Real patients. Real data.<br /><em>Real answers.</em>"
      ageBarTitle="Total Number of Unique Patients by Age Cohort"
      ageBarSubtitle="Total all-time claims by decade born &middot; IMX Data"
      ageLabels={data.ageCohorts.map((a) => a[0])}
      ageBarData={data.ageCohorts.map((a) => a[1] / 1e6)}
      ageBarColors={colorPalette}
      genderDoughnut={{
        title: `Gender Split &mdash; ${stats.totalClaimsM}M Claims`,
        subtitle: `${stats.femalePct}% Female &middot; ${stats.malePct}% Male &middot; ${stats.unknownPct}% Unknown`,
        labels: ['Female', 'Male', 'Unknown'],
        data: [...data.genderTotals],
        colors: ['#E8634A', '#11a0a5', '#2d3a37'],
        cutout: '55%',
        tooltipFormat: (v, _label, total) =>
          ` ${(v / 1e6).toFixed(1)}M (${total > 0 ? ((v / total) * 100).toFixed(1) : '0.0'}%)`,
        height: '122px',
      }}
      narrativeCards={glpS7NarrativeCards}
      insightHtml={glpS7InsightHtml}
    />

    <!-- SLIDE 8: OUTLOOK -->
    <StudyMarketOutlookSlide
      id="s8"
      extraBottom
      active={slideActive('s8')}
      eyebrow="Chapter 04 &middot; What's Coming"
      headline="The next five years will<br />reshape this market <em>completely.</em>"
      timelineItems={glpS8OutlookTimelineItems}
      spendLineTitle="Total Market Spend Trajectory"
      spendLineSubtitle="$USD billions quarterly &middot; IMX Data"
      quarterLabels={stats.quarterLabels}
      spendLineValues={glpS8SpendBillions}
      signalCalloutHtml={glpS8SignalCalloutHtml}
      statTiles={[
        { value: '190+', label: 'Assets in\nDevelopment' },
        { value: '<10%', label: 'WHO Est. Patient\nAccess by 2030', valueColor: 'var(--amb)' },
      ]}
    />

    <!-- SLIDE 8b: CHINA & EMERGING PIPELINE — only when ?additional_slide_tags includes glp-1-china -->
    {#if showGlp1ChinaSlide}
      <StudyEmergingPipelineSlide
        id="s8b"
        extraBottom
        eyebrow="Chapter 04 &middot; What's Coming"
        headline="China &amp; the <em>emerging</em> pipeline"
        panelEyebrowHtml="China regulatory"
        panelBodyHtml="<strong>Innovent</strong> secured China approval for <strong>mazdutide</strong> for chronic weight management &mdash; described publicly as the <strong>first dual glucagon + GLP-1</strong> agonist approved there for obesity. China rights were partnered from <strong>Eli Lilly</strong> (2020). Phase 3 data in type 2 diabetes showed glycemic and weight improvements vs placebo; diabetes labeling remains under regulatory review. Analysts have pointed to meaningful China sales potential this decade."
        pipelineRows={glpS8bPipelineRows}
        imxLensHtml={glpS8bImxLensHtml}
        disclaimerHtml={glpS8bDisclaimerHtml}
      />
    {/if}

    <!-- SLIDE 9: CLOSE -->
    <StudyClosingSlide
      id="s9"
      extraBottom
      {goTo}
      {disableLinks}
      headlineHtml="<em>The market is moving.</em><br />Are you moving<br />with it?"
      leadHtml="IMX Data delivers the depth behind the headlines."
      taglineHtml="Every drug &nbsp;&middot;&nbsp; Every state &nbsp;&middot;&nbsp; Every day."
    />
  {/snippet}
</StudyPresentation>

<style>
/* Slide s5a: two-column on desktop, single column on narrow viewports (matches StudyPresentation / slide shell 640px) */
.s5a-stack {
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 1fr);
  gap: 1.15rem;
  align-items: start;
  min-width: 0;
}
.s5a-box {
  min-width: 0;
}
.s5a-box--aside {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}
@media (max-width: 640px) {
  .s5a-stack {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
}

/* Slide s8 outlook: tighter vertical rhythm on laptop-height viewports */
:global(#s8 .tli) {
  padding: 0.5rem 0.78rem;
  margin-bottom: 0.32rem;
  gap: 0.62rem;
}
:global(#s8 .tt) {
  font-size: 0.72rem;
  line-height: 1.4;
}

/* s2b + s7: laptop-height viewports — tighter cards / charts so content clears the fixed nav */
@media (min-width: 1025px) and (max-height: 900px) {
  :global(.study-presentation .slide#s2b .tc),
  :global(.study-presentation .slide#s7 .tc) {
    gap: 1rem !important;
  }
  :global(.study-presentation .slide#s2b .tc > div),
  :global(.study-presentation .slide#s7 .tc > div) {
    gap: 0.55rem !important;
  }
  :global(.study-presentation .slide#s2b .ey),
  :global(.study-presentation .slide#s7 .ey) {
    margin-bottom: 0.32rem !important;
  }
  :global(.study-presentation .slide#s2b .hl),
  :global(.study-presentation .slide#s7 .hl) {
    margin-bottom: 0.65rem !important;
  }
  :global(.study-presentation .slide#s2b .cc),
  :global(.study-presentation .slide#s7 .cc) {
    padding: 0.78rem 0.9rem 0.72rem !important;
  }
  :global(.study-presentation .slide#s2b .ch) {
    height: 168px !important;
  }
  :global(.study-presentation .slide#s7 .tc > div:first-child > .cc:first-child .ch) {
    height: 152px !important;
  }
  :global(.study-presentation .slide#s7 .tc > div:first-child > .cc:nth-child(2) .ch) {
    height: 100px !important;
  }
  :global(.study-presentation .slide#s2b .sb) {
    padding: 0.65rem 0.7rem !important;
  }
  :global(.study-presentation .slide#s2b .sb .n) {
    font-size: 1.28rem !important;
  }
  :global(.study-presentation .slide#s2b .tli),
  :global(.study-presentation .slide#s7 .tli) {
    padding: 0.55rem 0.78rem !important;
    margin-bottom: 0.34rem !important;
  }
  :global(.study-presentation .slide#s2b .hb),
  :global(.study-presentation .slide#s7 .hb) {
    padding: 0.88rem 0.95rem !important;
  }
  :global(.study-presentation .slide#s7 .ca) {
    padding: 0.95rem 0.95rem !important;
  }
  :global(.study-presentation .slide#s7 .ca .ic) {
    font-size: 1.05rem !important;
    margin-bottom: 0.4rem !important;
  }
  :global(.study-presentation .slide#s7 .ca .vl) {
    font-size: 1.35rem !important;
  }
}
</style>
